import { Alert, Button, Input, Select } from '@piggytechng/component-library';
import { useMemo, useState } from 'react';
import { OverideButtonStyle, OverideInputStyle, OverideSelectStyle, formatToNaira } from '../../../../helpers/utils';
import useCustomers from '../../../../hooks/useCustomers';
import useWithdrawalActions from '../../../../hooks/useWithdrawalActions';
import useRolePermissions from '../../../../hooks/useRolePermissions';
import ModalExecuteAction from '../../../../components/Modals/ModalExecuteAction';
import { WithdrawForUserDestination, WithdrawForUserPenaltySource, WithdrawForUserWallet } from '../../../../types';

const destinations = [
  { value: 'bank', label: 'Bank' },
  { value: 'pocketapp', label: 'Pocket App' },
];

const penaltySources = [
  { value: 'wallet', label: 'From wallet balance' },
  { value: 'amount', label: 'From amount' },
];

const WithdrawForUser = ({ userId }: any) => {
  const { UserBalances, isLoadingUserBalances } = useCustomers();
  const { withdrawForUser, isWithdrawingForUser } = useWithdrawalActions();
  const { canPerformAction } = useRolePermissions({});

  const [wallet, setWallet] = useState<WithdrawForUserWallet | ''>('');
  const [amount, setAmount] = useState('');
  const [destination, setDestination] = useState<WithdrawForUserDestination | ''>('');
  const [penaltySource, setPenaltySource] = useState<WithdrawForUserPenaltySource | ''>('');
  const [showAuthModal, setShowAuthModal] = useState(false);

  const style = { ...OverideSelectStyle, style: { ...OverideSelectStyle.style, zIndex: undefined } };

  const isPiggybank = wallet === 'piggybank';

  const walletOptions = useMemo(() => {
    const withBalance = (label: string, balance?: number) =>
      isLoadingUserBalances || balance === undefined ? label : `${label} (${formatToNaira(balance)})`;

    return [
      { value: 'flex', label: withBalance('Flex', UserBalances?.flexNaira) },
      { value: 'piggybank', label: withBalance('Piggybank', UserBalances?.piggyBank) },
    ];
  }, [UserBalances, isLoadingUserBalances]);

  const selectedBalance = wallet === 'flex' ? UserBalances?.flexNaira : UserBalances?.piggyBank;
  const amountValue = Number(amount.replace(/,/g, '').replace('₦', ''));

  const resetForm = () => {
    setWallet('');
    setAmount('');
    setDestination('');
    setPenaltySource('');
    setShowAuthModal(false);
  };

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();

    if (!canPerformAction('withdrawalManager', 'withdrawForUser')) {
      Alert.warn('You do not have access to perform this action.');
      return;
    }
    if (!wallet) {
      Alert.error('Choose the wallet to withdraw from');
      return;
    }
    if (!amountValue || amountValue <= 0) {
      Alert.error('Enter an amount greater than zero');
      return;
    }
    if (typeof selectedBalance === 'number' && amountValue > selectedBalance) {
      Alert.error('Amount is more than the wallet balance');
      return;
    }
    if (!destination) {
      Alert.error('Choose where the money is going');
      return;
    }
    if (isPiggybank && !penaltySource) {
      Alert.error('Choose where the break fee comes from');
      return;
    }

    setShowAuthModal(true);
  };

  const executeWithdrawal = (password?: string) =>
    withdrawForUser(
      userId,
      {
        wallet: wallet as WithdrawForUserWallet,
        amount: amountValue,
        destination: destination as WithdrawForUserDestination,
        ...(isPiggybank ? { penaltysource: penaltySource as WithdrawForUserPenaltySource } : {}),
      },
      password ?? '',
      resetForm,
    );

  return (
    <form className="wallet-action" onSubmit={handleSubmit}>
      <Select
        label="Wallet Type"
        {...style}
        labelStyle={{ color: '#666766' }}
        backgroundColor="#F9FAFB"
        outlineColor="none"
        height="60px"
        placeholder="select wallet"
        value={wallet}
        options={walletOptions}
        key={`wallet-${walletOptions[0].label}`}
        onChange={(option: any) => {
          setWallet(option ?? '');
          setPenaltySource('');
        }}
      />

      <Input
        {...OverideInputStyle}
        label="Amount"
        className="user-form-input"
        height="60px"
        placeholder="Enter amount"
        backgroundColor="#F9FAFB"
        outlineColor="transparent"
        type="number"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />

      <Select
        label="Destination"
        {...style}
        labelStyle={{ color: '#666766' }}
        backgroundColor="#F9FAFB"
        outlineColor="none"
        height="60px"
        placeholder="Choose Destination"
        value={destination}
        options={destinations}
        onChange={(option: any) => setDestination(option ?? '')}
      />

      {isPiggybank && (
        <Select
          label="Break fee"
          {...style}
          labelStyle={{ color: '#666766' }}
          backgroundColor="#F9FAFB"
          outlineColor="none"
          height="60px"
          placeholder="Choose where the break fee comes from"
          value={penaltySource}
          options={penaltySources}
          onChange={(option: any) => setPenaltySource(option ?? '')}
        />
      )}

      <p className="wallet-action__helper">
        This withdrawal is recorded as made by an admin on the customer&apos;s behalf. A piggybank withdrawal attracts a
        break fee, taken either from the wallet balance or out of the amount withdrawn.
      </p>

      <div>
        <Button
          type="submit"
          loading={isWithdrawingForUser}
          {...OverideButtonStyle}
          style={{ fontFamily: 'Jakarta', width: '100%' }}
        >
          Withdraw
        </Button>
      </div>

      <ModalExecuteAction
        modalIsOpen={showAuthModal}
        isLoading={isWithdrawingForUser}
        setIsOpen={setShowAuthModal}
        text={`withdraw ${formatToNaira(amountValue || 0)} from this customer's ${
          isPiggybank ? 'piggybank' : 'flex'
        } to ${destination === 'pocketapp' ? 'Pocket App' : 'their bank'}?`}
        action={executeWithdrawal}
        actionColor="#1734CA"
        buttonText="Withdraw"
      />
    </form>
  );
};

export default WithdrawForUser;
